import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, downloadFile } from "../lib/api";
import { inr, quickRange } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileSpreadsheet, FileText, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// Deliberately excludes "bill": a suspense bill settlement converts an existing advance into an expense —
// no cash moves — so it never appears in this report and is not offered as a filter (the server rejects
// entry_type=bill outright, with the reason, rather than silently returning nothing).
const ENTRY_OPTIONS = [
  { v: "", l: "All entries" },
  { v: "cs", l: "Cash Slip" },
  { v: "cv", l: "Cash Voucher (direct expense)" },
  { v: "sa", l: "Suspense Advance (cash paid out)" },
  { v: "cr", l: "Suspense Cash Return (cash paid back)" },
  { v: "opening_adjustment", l: "Opening Adjustment" },
];

// Task 18: Source is the true origin of an entry, not who recorded it — 'manual' for everything a human
// posted by hand, or the payroll source type carried by a voucher raised from the pending panel. A fixed
// list, matching the server's own PETTYCASH_SOURCE_FILTERS exactly (server/ledger.php), not free text.
const SOURCE_OPTIONS = [
  { v: "", l: "All sources" },
  { v: "manual", l: "Manual" },
  { v: "pay_run", l: "Payroll — Pay Run" },
  { v: "advance", l: "Payroll — Cash Advance" },
  { v: "loan", l: "Payroll — Cash Loan" },
];

const EMPTY = { from_date: "", to_date: "", entry_type: "", doc_no: "", search: "", source: "", debit_only: false, credit_only: false };

// Wave review, Important #6: quickRange() used to be defined here from a UTC isoDate() (toISOString()),
// which is wrong for the first five and a half hours of every day in Asia/Kolkata (+05:30) — "Today"
// returned an empty report, and on the 1st of a month "This Month" produced from > to. Fixed once in
// lib/format.js on top of the existing local-time todayIso(), and shared with AccountsJournal.jsx, which
// had the identical bug from the identical copy-paste.

export default function PettyCashReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
      const { data } = await api.get("/reports/petty-cash", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(EMPTY); /* eslint-disable-next-line */ }, []);

  const reset = () => { setFilters(EMPTY); load(EMPTY); };
  const setQuick = (k) => {
    if (k === "all") { const f = { ...filters, from_date: "", to_date: "" }; setFilters(f); load(f); return; }
    const [from, to] = quickRange(k);
    const f = { ...filters, from_date: from, to_date: to };
    setFilters(f); load(f);
  };
  const setDebitOnly = (v) => { const f = { ...filters, debit_only: v, credit_only: v ? false : filters.credit_only }; setFilters(f); };
  const setCreditOnly = (v) => { const f = { ...filters, credit_only: v, debit_only: v ? false : filters.debit_only }; setFilters(f); };

  const download = async (kind) => {
    const ext = kind === "xlsx" ? "xlsx" : "pdf";
    const slug = filters.from_date && filters.to_date ? `${filters.from_date}_${filters.to_date}` : "all-time";
    try { await downloadFile(`/reports/petty-cash.${ext}`, filters, `petty-cash-report_${slug}.${ext}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (!data) return <div className="font-mono text-sm text-muted">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/reports" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink mb-2">
          <ArrowLeft size={14} /> Back to Reports
        </Link>
        <div className="label-eyebrow">Reports</div>
        <h1 className="font-heading text-4xl font-bold mt-1">Petty Cash Report</h1>
        <p className="text-sm text-muted mt-1">A pure cash-movement view — date, particulars, debit, credit and the running cash on hand. No suspense figures.</p>
      </div>

      <Card className="p-5 border border-line">
        <div className="flex gap-2 flex-wrap mb-4">
          {[["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["fy", "FY (Financial Year)"], ["all", "All Time"]].map(([k, l]) => (
            <Button key={k} variant="outline" size="sm" onClick={() => setQuick(k)} data-testid={`pcr-quick-${k}`}>{l}</Button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="label-eyebrow">From</Label>
            <Input type="date" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value })} className="mt-1.5 font-mono" data-testid="pcr-from" /></div>
          <div><Label className="label-eyebrow">To</Label>
            <Input type="date" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value })} className="mt-1.5 font-mono" data-testid="pcr-to" /></div>
          <div><Label className="label-eyebrow">Transaction Type</Label>
            <Select value={filters.entry_type || "_all"} onValueChange={v => setFilters({ ...filters, entry_type: v === "_all" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="pcr-entry-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ENTRY_OPTIONS.map(o => <SelectItem key={o.v || "_all"} value={o.v || "_all"}>{o.l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="label-eyebrow">Voucher No.</Label>
            <Input value={filters.doc_no} onChange={e => setFilters({ ...filters, doc_no: e.target.value })} placeholder="CS 001 / CV 002 / SA 003" className="mt-1.5 font-mono" data-testid="pcr-doc-no" /></div>
          <div className="md:col-span-2"><Label className="label-eyebrow">Search Particulars</Label>
            <Input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="e.g. wages, replenishment" className="mt-1.5" data-testid="pcr-search" /></div>
          <div><Label className="label-eyebrow">Source</Label>
            <Select value={filters.source || "_all"} onValueChange={v => setFilters({ ...filters, source: v === "_all" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="pcr-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCE_OPTIONS.map(o => <SelectItem key={o.v || "_all"} value={o.v || "_all"}>{o.l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="flex items-center gap-2 mt-5">
            <Checkbox id="pcr-debit-only" checked={filters.debit_only} onCheckedChange={setDebitOnly} data-testid="pcr-debit-only" />
            <Label htmlFor="pcr-debit-only" className="cursor-pointer">Debit only (cash received)</Label>
          </div>
          <div className="flex items-center gap-2 mt-5">
            <Checkbox id="pcr-credit-only" checked={filters.credit_only} onCheckedChange={setCreditOnly} data-testid="pcr-credit-only" />
            <Label htmlFor="pcr-credit-only" className="cursor-pointer">Credit only (cash paid out)</Label>
          </div>
          <div className="flex gap-2 items-end">
            <Button onClick={() => load()} disabled={loading} data-testid="pcr-apply" className="bg-ink text-white">Apply</Button>
            <Button onClick={reset} variant="outline"><RotateCcw size={14} className="mr-1" /> Reset</Button>
          </div>
          <div className="flex gap-2 items-end justify-end md:col-span-2">
            <Button onClick={() => download("xlsx")} variant="outline" data-testid="pcr-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
            <Button onClick={() => download("pdf")} variant="outline" data-testid="pcr-pdf"><FileText size={14} className="mr-1" /> PDF</Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Opening Balance" v={data.opening_balance} testid="pcr-opening" />
        <Stat label="Total Debit" v={data.total_debit} accent="in" testid="pcr-total-debit" />
        <Stat label="Total Credit" v={data.total_credit} accent="out" testid="pcr-total-credit" />
        <Stat label="Closing Balance" v={data.closing_balance} strong testid="pcr-closing" />
      </div>

      <Card className="overflow-hidden border border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-paper-alt border-b border-line">
              {["Date", "Particulars", "Debit", "Credit", "O/B"].map((h, i) => (
                <th key={h} className={`px-4 py-3 label-eyebrow whitespace-nowrap ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody data-testid="pcr-rows">
              {data.rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-muted">No entries in this filter.</td></tr> :
                data.rows.map(r => (
                  <tr key={r.id} className="border-b border-line hover:bg-paper/60">
                    <td className="px-4 py-2.5 font-mono whitespace-nowrap">{r.date}</td>
                    <td className="px-4 py-2.5">{r.particulars}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-cashin whitespace-nowrap">{r.debit ? inr(r.debit) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-cashout whitespace-nowrap">{r.credit ? inr(r.credit) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold whitespace-nowrap">{inr(r.balance)}</td>
                  </tr>
                ))}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr className="bg-paper-alt border-t-2 border-line">
                  <td colSpan={2} className="px-4 py-2.5 label-eyebrow">Totals</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold whitespace-nowrap">{inr(data.total_debit)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold whitespace-nowrap">{inr(data.total_credit)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold whitespace-nowrap">{inr(data.closing_balance)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, v, accent, strong, testid }) {
  const c = accent === "in" ? "text-cashin" : accent === "out" ? "text-cashout" : "text-ink";
  return (
    <Card className="p-4 border border-line overflow-hidden" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 font-mono tabular-nums whitespace-nowrap ${strong ? "text-lg sm:text-xl font-bold" : "text-sm sm:text-base font-semibold"} ${c}`}>{inr(v)}</div>
    </Card>
  );
}
